import { useTranslation } from 'react-i18next';

import { useSelect } from '../hooks/useCoreContext';
import { Option as OptionType } from '@/legacy/types';
import { Checkbox, type CheckboxShape } from '@frekuent/orbit/components';
import { NonTranslatableText } from '@/shared/ui/NonTranslatableText';

const Option = ({
	option,
	index,
	checkBoxShape = 'round',
}: {
	option: OptionType;
	index: number;
	checkBoxShape?: CheckboxShape;
}) => {
	const { t } = useTranslation();
	const {
		toggleItem,
		highlightedIndex,
		setHighlightedIndex,
		isSelected,
		multiple,
		ids,
		getItemProps,
		listRef,
		isAllActive,
		interactionModeRef,
	} = useSelect();

	const mutedOption = isAllActive;

	const selected =
		typeof option.value === 'string' || typeof option.value === 'number'
			? isSelected(option.value)
			: false;
	const highlighted = highlightedIndex === index;
	const optionLabel = option.notTranslatable ? (
		<NonTranslatableText>{option.label}</NonTranslatableText>
	) : (
		t(option.label)
	);

	return (
		<li
			ref={(node) => {
				listRef.current[index] = node;
			}}
			{...getItemProps()}
			aria-selected={selected}
			aria-disabled={option.disabled}
			tabIndex={-1}
			id={ids.option(index)}
			className={`p-4 rounded overflow-hidden
                ${option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                ${highlighted && !option.disabled ? 'bg-secondary-ultra-light' : ''}
                ${!option.disabled ? 'hover:bg-secondary-ultra-light' : ''}
                flex gap-2
                `}
			onPointerEnter={(event) => {
				if (option.disabled) return;
				if (event.pointerType !== 'mouse') return;
				interactionModeRef.current = 'pointer';
				setHighlightedIndex(index);
			}}
			onClick={(e) => {
				e.stopPropagation();
				if (option.disabled) return;
				toggleItem(option.value);
			}}
		>
			{multiple && (
				<Checkbox
					isChecked={!!selected}
					shape={checkBoxShape}
					highlightIndex={highlighted}
				/>
			)}
			<span
				className={`${mutedOption ? 'text-tertiary' : ''} first-letter:uppercase min-w-0 truncate`}
			>
				{option.renderLabel ?? optionLabel}
			</span>
		</li>
	);
};

export default Option;
